/**
 * Fountain Entity Configuration
 *
 * @version 1.0
 * @updated 2026-02-27
 * @module core/modules/environmental/config
 *
 * @description
 * Configuración de la entidad "Fuente/Bebedero" para el entity registry.
 * Define API, UI, factory y metadatos para fuentes y bebederos.
 */

import { Droplets } from 'lucide-react';
import type { EntityConfig } from '../../../entity-registry';
import type { Fountain } from '../../../map-tools/fountain-types';
import { FountainDetailsPanel } from '../../../map-tools/FountainDetailsPanel';
import { fountainsApi } from '../../../map-tools/fountains-api';

/**
 * Crea una fuente con valores por defecto
 */
function createDefaultFountain(coordinates: [number, number]): Fountain {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 15);

  return {
    id: `fountain-${timestamp}-${randomSuffix}`,
    code: `TEMP-FOUNTAIN-${timestamp}`,
    coordinates,
    createdAt: new Date(),
    updatedAt: new Date(),

    // General - campos obligatorios
    type: 'drinking', // Tipo por defecto: bebedero

    // General - campos opcionales
    name: undefined,
    address: undefined,
    neighborhood: undefined,
    locationType: undefined,
    installationDate: undefined,
    material: undefined,
    style: undefined,

    // Operational
    status: 'active',
    waterQuality: undefined,
    lastQualityTest: undefined,
    flowRate: undefined,
    hasFilter: false,
    filterType: undefined,
    lastFilterChange: undefined,
    hasDrainage: false,
    drainageCondition: undefined,
    surroundingCondition: undefined,
    hasLighting: false,

    // Maintenance
    lastMaintenance: undefined,
    maintenanceType: undefined,
    maintenanceNotes: undefined,
    nextScheduledMaintenance: undefined,
    assignedResponsible: undefined,
    needsIntervention: false,
    interventionType: undefined,
    priority: undefined,

    // Accessibility
    accessibilityLevel: undefined,
    hasWheelchairAccess: false,
    hasPetBowl: false,
    childFriendly: false,
    hasHistoricalValue: false,
    historicalDescription: undefined,
    isProtected: false,
    protectionLaw: undefined,
    owner: undefined,
    managementArea: undefined,
    generalNotes: undefined,
  };
}

/**
 * Configuración de la entidad "Fuente/Bebedero"
 */
export const fountainEntityConfig: EntityConfig<Fountain> = {
  type: 'fountain',
  displayName: 'fuente',
  toolId: 'create-fountain',

  apiService: {
    create: fountainsApi.create.bind(fountainsApi),
    update: fountainsApi.update.bind(fountainsApi),
    delete: fountainsApi.delete.bind(fountainsApi),
  },

  detailsPanel: FountainDetailsPanel,
  icon: Droplets,

  createDefault: createDefaultFountain,
  tempIdPrefix: 'fountain-',
  codePrefix: 'FT',

  // Funciones extractoras
  getId: (fountain) => fountain.id,
  getCoordinates: (fountain) => fountain.coordinates,
  getCode: (fountain) => fountain.code,
};
